// ** react imports
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';

// ** react router imports
import { useNavigate, useParams } from 'react-router-dom';

// ** redux imports
import { useSelector } from 'react-redux';

// ** validation imports
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import {
  useUpdateAnnouncement,
  useGetAnnouncementById,
} from '../../../hooks/apis/useAnnouncement';

// ** components imports
import Loading from '../../../components/common/Loading';
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import SwitchInput from '../../../components/common/Inputs/SwitchInput';
import Textarea from '../../../components/common/Inputs/Textarea';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const RenderFormSection = props => {
  const { children } = props;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
      <div className="p-4">{children}</div>
    </div>
  );
};

const UpdateAnnouncement = () => {
  // hooks
  const navigate = useNavigate();
  const { id } = useParams();

  // api calls and mutations
  const { isUpdateAnnouncementLoading, updateAnnouncement } =
    useUpdateAnnouncement();
  const {
    data: announcementData,
    isGetAnnouncementByIdLoading,
    getAnnouncementById,
  } = useGetAnnouncementById();

  // validation schema
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required('Title is required')
      .max(20, 'Title must be at most 200 characters')
      .trim(),
    description: yup.string().required('Description is required').trim(),
    publishDate: yup
      .string()
      .required('Publish date is required')
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Publish date must be in YYYY-MM-DD format'
      ),
    expiryDate: yup
      .string()
      .nullable()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Expiry date must be in YYYY-MM-DD format'
      ),
    urgency: yup
      .string()
      .nullable()
      .oneOf(
        ['low', 'medium', 'high', 'critical'],
        'Urgency must be low, medium, high, or critical'
      ),
    category: yup
      .string()
      .nullable()
      .oneOf(
        ['info', 'warning', 'error', 'success', 'maintenance'],
        'Category must be info, warning, error, success, or maintenance'
      ),
    isActive: yup.boolean().nullable(),
    targetAudiences: yup
      .array()
      .of(yup.string().oneOf(['all', 'customer', 'manager', 'admin']))
      .nullable()
      .default([]),
    priority: yup
      .number()
      .nullable()
      .integer('Priority must be an integer')
      .min(0, 'Priority must be a non-negative integer'),
    isGlobal: yup.boolean().nullable(),
    isPinned: yup.boolean().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues:
      {
        ...announcementData,
        publishDate: announcementData?.publishDate
          ? new Date(announcementData.publishDate).toISOString().split('T')[0]
          : '',
        expiryDate: announcementData?.expiryDate
          ? new Date(announcementData.expiryDate).toISOString().split('T')[0]
          : '',
      } || {},
  });

  // Update form values when announcement data is loaded
  useEffect(() => {
    getAnnouncementById(id);
  }, [id]);
  useEffect(() => {
    if (announcementData) {
      // Determine the correct data structure
      // Some APIs return data directly, others nest it in a 'data' property
      const actualData = announcementData.data || announcementData;

      // Set each field individually using setValue
      Object.keys(actualData).forEach(key => {
        if (key === 'publishDate' || key === 'expiryDate') {
          // Format dates to YYYY-MM-DD for date inputs
          if (actualData[key]) {
            const date = new Date(actualData[key]);
            const formattedDate = date.toISOString().split('T')[0];
            setValue(key, formattedDate);
          } else {
            setValue(key, '');
          }
        } else if (key === 'targetAudiences') {
          // Handle array fields like targetAudiences
          setValue(key, actualData[key] || []);
        } else if (
          key === 'isActive' ||
          key === 'isGlobal' ||
          key === 'isPinned'
        ) {
          // Handle boolean fields
          setValue(
            key,
            actualData[key] !== undefined ? actualData[key] : false
          );
        } else if (key === 'priority') {
          // Handle number fields
          setValue(key, actualData[key] !== undefined ? actualData[key] : 0);
        } else if (validationSchema.fields[key]) {
          // Set other fields if they exist in the validation schema
          setValue(key, actualData[key] !== undefined ? actualData[key] : '');
        }
      });
    }
  }, [announcementData]);

  const onSubmit = async data => {
    const updatedAnnouncement = {
      ...announcementData,
      ...data,
    };

    const response = await updateAnnouncement({
      id,
      data: updatedAnnouncement,
    });

    if (response.data?.success) {
      toast.success('Announcement updated successfully');
      navigate('/announcements/list');
    } else {
      toast.error(
        response.error?.data?.message || 'Failed to update announcement'
      );
    }
  };

  const handleCancel = () => {
    navigate('/announcements/list');
  };

  const renderErrorMessage = error => {
    if (!error) return null;
    return <p className="mt-2 text-xs text-red-500">{error}</p>;
  };

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            {/* Left: Title */}
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Update Announcement
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Update an existing announcement with new details
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl mb-8">
            {isGetAnnouncementByIdLoading ? (
              <div className="w-full min-h-[calc(88vh-100px)] flex items-center justify-center bg-white/50 dark:bg-gray-800/50">
                <Loading />
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-6 space-y-6">
                  {/* Primary Information Section */}
                  <RenderFormSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <NormalInput
                          label="Title"
                          type="text"
                          name="title"
                          register={register}
                          errors={errors.title?.message}
                          required
                          maxLength={200}
                          placeholder="Enter announcement title"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Textarea
                          label="Description"
                          name="description"
                          register={register}
                          errors={errors.description?.message}
                          required
                          placeholder="Enter announcement description"
                          rows={4}
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Publish Date"
                          type="date"
                          name="publishDate"
                          register={register}
                          errors={errors.publishDate?.message}
                          required
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Expiry Date"
                          type="date"
                          name="expiryDate"
                          register={register}
                          errors={errors.expiryDate?.message}
                          placeholder="Optional"
                        />
                      </div>

                      <div>
                        <SelectInput
                          label="Urgency"
                          name="urgency"
                          options={[
                            { label: 'Low', value: 'low' },
                            { label: 'Medium', value: 'medium' },
                            { label: 'High', value: 'high' },
                            { label: 'Critical', value: 'critical' },
                          ]}
                          register={register}
                          errors={errors.urgency?.message}
                        />
                      </div>

                      <div>
                        <SelectInput
                          label="Category"
                          name="category"
                          options={[
                            { label: 'Info', value: 'info' },
                            { label: 'Warning', value: 'warning' },
                            { label: 'Error', value: 'error' },
                            { label: 'Success', value: 'success' },
                            { label: 'Maintenance', value: 'maintenance' },
                          ]}
                          register={register}
                          errors={errors.category?.message}
                        />
                      </div>

                      <div>
                        <NormalInput
                          label="Priority"
                          type="number"
                          name="priority"
                          register={register}
                          errors={errors.priority?.message}
                          min="0"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </RenderFormSection>

                  {/* Targeting & Visibility Section */}
                  <RenderFormSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SwitchInput
                              label="Is Active"
                              checked={value}
                              onChange={e => onChange(e.target.checked)}
                              errors={errors.isActive?.message}
                            />
                          )}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Controller
                          name="targetAudiences"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Target Audiences
                              </label>
                              <div className="flex flex-wrap gap-3">
                                {[
                                  { label: 'All', value: 'all' },
                                  {
                                    label: 'Customer',
                                    value: 'customer',
                                  },
                                  {
                                    label: 'Manager',
                                    value: 'manager',
                                  },
                                  {
                                    label: 'Admin',
                                    value: 'admin',
                                  },
                                ].map(option => (
                                  <label
                                    key={option.value}
                                    className="flex items-center"
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-blue-600"
                                      checked={
                                        value?.includes(option.value) || false
                                      }
                                      onChange={e => {
                                        if (e.target.checked) {
                                          onChange([
                                            ...(value || []),
                                            option.value,
                                          ]);
                                        } else {
                                          onChange(
                                            value?.filter(
                                              v => v !== option.value
                                            ) || []
                                          );
                                        }
                                      }}
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                      {option.label}
                                    </span>
                                  </label>
                                ))}
                              </div>
                              {renderErrorMessage(
                                errors.targetAudiences?.message
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </RenderFormSection>

                  {/* Form Footer */}
                  <footer>
                    <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
                      <div className="flex self-end gap-3">
                        <NormalButton
                          type="button"
                          theme={BUTTON_THEMES.DANGER}
                          onClick={() => handleCancel()}
                        >
                          Cancel
                        </NormalButton>
                        <LoadingButton
                          type="submit"
                          theme={BUTTON_THEMES.PRIMARY}
                          isLoading={isUpdateAnnouncementLoading}
                        >
                          Update Announcement
                        </LoadingButton>
                      </div>
                    </div>
                  </footer>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UpdateAnnouncement;
